import {
  Box,
  Flex,
  Grid,
  Icon,
  StyleProps,
  Text,
  useBreakpointValue,
  useToken,
} from "@chakra-ui/react";
import React from "react";
import { HiStar } from "react-icons/hi";

import {
  AiInterviewerListItemFragment,
  useAiInterviewerDataQuery,
} from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useIsScreenOrgAtsConnected } from "../../hooks/useIsScreenOrgAtsConnected";
import {
  getStructuredFormatFromValue,
  StructuredFormattedValue,
} from "../../pages/analytics/utils";
import {
  QUESTION_SCORE_COLORS,
  QuestionScoreLevel,
} from "../AiRecording/Evaluation/scoring";

type AiInterviewerCandidateScoresProps = {
  aiInterviewer: Pick<AiInterviewerListItemFragment, "id">;
};

export const AiInterviewerCandidateScores: React.FC<
  AiInterviewerCandidateScoresProps
> = ({ aiInterviewer }) => {
  const { data, loading, error } = useAiInterviewerDataQuery({
    variables: { aiInterviewerId: aiInterviewer.id },
  });
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false });
  const isAtsLess = !useIsScreenOrgAtsConnected();
  const showRating = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:insights"
  );

  if (loading) {
    return (
      <Box p="4" bg="gray.50" borderRadius="lg">
        <Text color="gray.500">Loading scores...</Text>
      </Box>
    );
  }

  if (error || !data?.aiInterviewerData) {
    return (
      <Box p="4" bg="red.50" borderRadius="lg">
        <Text color="red.500">Failed to load scores</Text>
      </Box>
    );
  }

  const scores = data.aiInterviewerData;

  const { scoreCounts, totalScoredQuestions } = scores;

  // Score legend items for the candidate scores section
  const scoreLegendItems = [
    {
      key: "excellent",
      label: "Excellent",
      scoreLevel: QuestionScoreLevel.EXCELLENT,
    },
    { key: "good", label: "Good:", scoreLevel: QuestionScoreLevel.GOOD },
    { key: "fair", label: "Fair:", scoreLevel: QuestionScoreLevel.FAIR },
    {
      key: "poor",
      label: "Below Avg.:",
      scoreLevel: QuestionScoreLevel.POOR,
    },
  ];

  const calculatePercentage = (
    count: number | null | undefined,
    total: number | null | undefined
  ): number => {
    if (!count || !total || total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  const renderScoreLegend = () => (
    <Flex direction="column" gap="1">
      {scoreLegendItems.map(({ key, label, scoreLevel }) => {
        type ScoreKey = "excellent" | "good" | "fair" | "poor";
        const count = scoreCounts[key as ScoreKey] || 0;
        const pct = calculatePercentage(count, totalScoredQuestions);
        return (
          <Flex key={key} align="center" gap="1">
            <Box
              w="2"
              h="2"
              bg={QUESTION_SCORE_COLORS[scoreLevel]}
              borderRadius="full"
              flexShrink={0}
            />
            <Text fontSize="10px" color="gray.600" whiteSpace="nowrap">
              {label} {pct}% ({count})
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );

  // Tablet layout: 3-column with 2x2 stats grid + scores chart side by side
  if (isTablet) {
    return (
      <Grid
        gridTemplateColumns="1fr 1fr 1fr"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        width="100%"
        px={4}
        py={3}
      >
        {/* 2x2 stats grid spanning 2 columns */}
        <Grid gridColumn="span 2" gridTemplateColumns="1fr 1fr" gap={4}>
          {!isAtsLess && (
            <CompactStat label="Invited" value={scores.totalInvitations} />
          )}
          <CompactStat label="Started" value={scores.totalStarted} />
          <CompactStat label="Completed" value={scores.totalInterviews} />
          <CompactStat
            label="Time Saved"
            value={getStructuredFormatFromValue(
              scores.totalDurationSeconds,
              "hours-minutes"
            )}
            isTimeSaved
          />
          {showRating && scores.candidateRatingCount > 0 && (
            <Flex direction="column">
              <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                Rating
              </Text>
              <Flex align="center" gap="1">
                <Text
                  color="gray.800"
                  fontWeight="semibold"
                  fontSize="xl"
                  as="span"
                >
                  {scores.averageCandidateRating?.toFixed(1) ?? "—"}
                </Text>
                <Icon as={HiStar} boxSize="18px" color="#FFA300" />
                <Text fontSize="xs" color="#67717E" as="span">
                  ({scores.candidateRatingCount}{" "}
                  {scores.candidateRatingCount === 1 ? "rating" : "ratings"})
                </Text>
              </Flex>
            </Flex>
          )}
        </Grid>
        {/* Scores chart in third column */}
        <Flex direction="column" pl={4}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb="2">
            CANDIDATE SCORES
          </Text>
          <Flex direction="row" align="center" gap="3">
            <ScoreDonutChart
              scoreCounts={scoreCounts}
              averageScore={scores.averageScore}
            />
            {renderScoreLegend()}
          </Flex>
        </Flex>
      </Grid>
    );
  }

  // Desktop layout (default)
  const showRatingColumn = showRating && scores.candidateRatingCount > 0;
  const columnCount =
    (isAtsLess ? 0 : 1) + 2 + (showRatingColumn ? 1 : 0) + 1 + 1;
  return (
    <Grid
      gridTemplateColumns={`repeat(${columnCount - 2}, 1fr) 1.5fr 1fr`}
      gridTemplateRows="auto 1fr"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      minW="0"
      w="100%"
      pt={5}
      pb={6}
    >
      {/* Row 1: Labels */}
      {!isAtsLess && (
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" px={6}>
          CANDIDATES INVITED
        </Text>
      )}
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" px={6}>
        INTERVIEWS STARTED
      </Text>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" px={6}>
        INTERVIEWS COMPLETED
      </Text>
      {showRatingColumn && (
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="gray.500"
          px={6}
          textAlign="center"
        >
          RATING
        </Text>
      )}
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" px={6}>
        CANDIDATE SCORES
      </Text>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500" px={6}>
        TIME SAVED
      </Text>

      {/* Row 2: Values */}
      {!isAtsLess && (
        <Text color="gray.800" fontWeight="400" fontSize="2.5em" px={6} pt={1}>
          {scores.totalInvitations}
        </Text>
      )}
      <Text color="gray.800" fontWeight="400" fontSize="2.5em" px={6} pt={1}>
        {scores.totalStarted}
      </Text>
      <Text color="gray.800" fontWeight="400" fontSize="2.5em" px={6} pt={1}>
        {scores.totalInterviews}
      </Text>
      {showRatingColumn && (
        <Flex direction="column" align="center" px={6} pt={1}>
          <Flex align="end" gap="1">
            <Text color="gray.800" fontWeight="400" fontSize="2.5em">
              {scores.averageCandidateRating?.toFixed(1) ?? "—"}
            </Text>
            <Icon as={HiStar} boxSize="32px" color="#FFA300" mb="8px" />
          </Flex>
          <Text fontSize="11.67px" fontWeight="500" color="#67717E" mt="1">
            ({scores.candidateRatingCount.toLocaleString()}{" "}
            {scores.candidateRatingCount === 1 ? "rating" : "ratings"})
          </Text>
        </Flex>
      )}
      <Flex
        direction="row"
        align="center"
        justify="start"
        gap="4"
        px={6}
        pt={1}
      >
        <ScoreDonutChart
          scoreCounts={scoreCounts}
          averageScore={scores.averageScore}
        />
        {renderScoreLegend()}
      </Flex>
      <Box px={6} pt={1}>
        <Text color="gray.800" fontWeight="400">
          {getStructuredFormatFromValue(
            scores.totalDurationSeconds,
            "hours-minutes"
          ).map((v, index, arr) => (
            <span key={v.unitSymbol}>
              <span style={{ fontSize: "2.5em" }}>{v.value}</span>
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "var(--chakra-colors-gray-900)",
                }}
              >
                {v.unitSymbol}
              </span>
              {index < arr.length - 1 && " "}
            </span>
          ))}
        </Text>
      </Box>
    </Grid>
  );
};

type CompactStatProps = {
  label: string;
  value: string | number | StructuredFormattedValue[];
  isTimeSaved?: boolean;
} & StyleProps;

const CompactStat: React.FC<CompactStatProps> = ({
  label,
  value,
  isTimeSaved,
  ...styleProps
}) => {
  return (
    <Flex direction="column" {...styleProps}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500">
        {label}
      </Text>
      {isTimeSaved && Array.isArray(value) ? (
        <Text color="gray.800" fontWeight="semibold" fontSize="xl">
          {value.map((v, index) => (
            <span key={v.unitSymbol}>
              {v.value}
              <span style={{ fontSize: "12px", fontWeight: 500 }}>
                {v.unitSymbol}
              </span>
              {index < value.length - 1 && " "}
            </span>
          ))}
        </Text>
      ) : (
        <Text color="gray.800" fontWeight="semibold" fontSize="xl">
          {value as string | number}
        </Text>
      )}
    </Flex>
  );
};

type ScoreCountsType = {
  excellent: number;
  good: number;
  fair: number;
  poor: number;
};

type ScoreDonutChartProps = {
  scoreCounts: ScoreCountsType;
  averageScore?: number | null;
};

const ScoreDonutChart: React.FC<ScoreDonutChartProps> = ({
  scoreCounts,
  averageScore,
}) => {
  // Resolve theme tokens to CSS color values for use in conic-gradient
  const [excellentColor, goodColor, fairColor, poorColor] = useToken("colors", [
    QUESTION_SCORE_COLORS[QuestionScoreLevel.EXCELLENT],
    QUESTION_SCORE_COLORS[QuestionScoreLevel.GOOD],
    QUESTION_SCORE_COLORS[QuestionScoreLevel.FAIR],
    QUESTION_SCORE_COLORS[QuestionScoreLevel.POOR],
  ]);

  const total =
    (scoreCounts.excellent || 0) +
    (scoreCounts.good || 0) +
    (scoreCounts.fair || 0) +
    (scoreCounts.poor || 0);

  // Calculate cumulative percentages for conic-gradient
  const excellentPct =
    total > 0 ? ((scoreCounts.excellent || 0) / total) * 100 : 0;
  const goodPct = total > 0 ? ((scoreCounts.good || 0) / total) * 100 : 0;
  const fairPct = total > 0 ? ((scoreCounts.fair || 0) / total) * 100 : 0;

  const p1 = excellentPct;
  const p2 = p1 + goodPct;
  const p3 = p2 + fairPct;

  const gradient = `conic-gradient(
    ${excellentColor} 0% ${p1}%,
    ${goodColor} ${p1}% ${p2}%,
    ${fairColor} ${p2}% ${p3}%,
    ${poorColor} ${p3}% 100%
  )`;

  const displayScore =
    averageScore != null && total > 0 ? Math.round(averageScore) : "--";

  return (
    <Box
      w="72px"
      h="72px"
      borderRadius="full"
      background={total === 0 ? "gray.100" : gradient}
      position="relative"
      flexShrink={0}
    >
      <Flex
        position="absolute"
        top="15%"
        left="15%"
        right="15%"
        bottom="15%"
        bg="white"
        borderRadius="full"
        align="center"
        justify="center"
      >
        <Text fontSize="xl" fontWeight="500" color="#D4A843">
          {displayScore}
        </Text>
      </Flex>
    </Box>
  );
};
